import classNames from "classnames"

import { Color } from "../../../constants/V2/color"

import Link from "@components/elements/Link"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import {
  getAccentColor,
  getBackgroundColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
} from "@utils/V2/color"
import React from "react"

type InfoCardItem = {
  iconUrl: string
  iconAlt: string
  title: string
  description: string
  backgroundColor: Color
}

type WorksWithItem = {
  text: string
  iconUrl: string
  iconAlt: string
  link?: string | YocoCom.YocoLink
}

type PlanDetails = {
  title: string
  subtitle?: string
  monthlyCost: string
  priceSubscript?: string
}

type PlansBannerProps = {
  superscript: string
  superscriptHighlightedWord?: string
  superscriptHighlightColor?: Color
  title: string
  description: string
  infoCards?: InfoCardItem[]
  worksWith?: WorksWithItem[]
  plan?: PlanDetails
  priceSubscript?: string
  priceSubscriptHighlight?: string
  priceSubscriptHighlightColor?: Color
  backgroundColor: Color
  secondaryBackgroundColor?: Color
  sectionColor?: Color
  primaryCtaText: string
  primaryCtaLink: string | YocoCom.YocoLink
  secondaryCtaText?: string
  secondaryCtaLink?: string | YocoCom.YocoLink
  id?: string
}

const PlansBanner = ({
  superscript,
  superscriptHighlightedWord,
  superscriptHighlightColor = Color.Blue,
  title,
  description,
  infoCards,
  worksWith,
  plan,
  priceSubscript,
  priceSubscriptHighlight,
  priceSubscriptHighlightColor = Color.Blue,
  backgroundColor,
  secondaryBackgroundColor,
  sectionColor = Color.White,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  id,
  ...props
}: PlansBannerProps) => {
  const accentColor = getAccentColor(backgroundColor)
  const hasWorksWith = !!worksWith?.length
  const hasInfoCards = !!infoCards?.length

  return (
    <Section
      id={id}
      className={classNames("py-3xl", getBackgroundColorClass(sectionColor))}
      {...props}
    >
      <div
        className={classNames(
          "flex flex-col gap-40 overflow-hidden rounded-20 border border-charcoal/10 p-24 md:p-40 lg:p-60",
          secondaryBackgroundColor
            ? [
                "bg-gradient-to-br",
                getGradientFromColorClass(backgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
            : getBackgroundColorClass(backgroundColor)
        )}
      >
        <div className="flex w-full flex-col gap-4 md:w-5/6">
          <Typography
            text={superscript}
            size="body-md"
            font="grotesk"
            weight="medium"
            color={accentColor}
            highlightedText={superscriptHighlightedWord}
            highlightColor={superscriptHighlightColor}
          />
          <Typography
            text={title}
            size="h4"
            font="grotesk"
            weight="medium"
            color={accentColor}
          />
          <Typography
            text={description}
            size="body-lg"
            font="grotesk"
            weight="book"
            color={accentColor}
            className="opacity-80"
          />
        </div>

        {hasInfoCards && (
          <div className="grid grid-cols-1 gap-20 sm:grid-cols-3 sm:gap-32">
            {infoCards?.map((card, index) => (
              <div
                key={`info-card-${card.title}-${index}`}
                className={classNames(
                  "flex flex-col gap-20 rounded-10 p-24",
                  getBackgroundColorClass(card.backgroundColor)
                )}
              >
                <GatsbyStoryblokImage
                  image={card.iconUrl}
                  alt={card.iconAlt}
                  aspectRatio="1:1"
                  width={48}
                  height={48}
                  className="size-48"
                />
                <div className="flex flex-col gap-[2px]">
                  <Typography
                    text={card.title}
                    size="lead-sm"
                    font="grotesk"
                    weight="medium"
                    color={getAccentColor(card.backgroundColor)}
                  />
                  <Typography
                    text={card.description}
                    size="body-md"
                    font="grotesk"
                    weight="book"
                    color={getAccentColor(card.backgroundColor)}
                    className="opacity-80"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-40 lg:flex-row lg:items-center lg:justify-between">
          {hasWorksWith && (
            <div className="flex flex-col gap-[19px]">
              <Typography
                text="WORKS WITH"
                size="body-md"
                font="grotesk"
                weight="medium"
                color={accentColor}
                className="uppercase"
              />
              <div className="flex flex-wrap gap-16">
                {worksWith?.map((item, index) => {
                  const itemContent = (
                    <>
                      <div className="rounded-6 size-64 overflow-hidden">
                        <GatsbyStoryblokImage
                          image={item.iconUrl}
                          alt={item.iconAlt}
                          aspectRatio="1:1"
                          width={64}
                          height={64}
                          className="size-64 transition-transform duration-500 ease-out group-hover:scale-105"
                          imageObjectFit="contain"
                        />
                      </div>
                      <Typography
                        text={item.text}
                        size="body-sm"
                        font="grotesk"
                        weight="medium"
                        color={Color.Black}
                        className="text-center"
                      />
                    </>
                  )

                  const itemClassName = classNames(
                    "group flex w-[110px] flex-col items-center gap-8 rounded-10 bg-white px-10 pb-10 pt-8 drop-shadow-[0px_10px_10px_rgba(0,0,0,0.05)]",
                    { "cursor-pointer": !!item.link }
                  )

                  if (item.link) {
                    return (
                      <Link
                        key={`works-with-${item.text}-${index}`}
                        link={item.link}
                        className={itemClassName}
                      >
                        {itemContent}
                      </Link>
                    )
                  }

                  return (
                    <div
                      key={`works-with-${item.text}-${index}`}
                      className={itemClassName}
                    >
                      {itemContent}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-20">
            {plan && (
              <div className="flex flex-col gap-[2px]">
                <Typography
                  text={plan.title}
                  size="lead-sm"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                />
                {plan.subtitle && (
                  <Typography
                    text={plan.subtitle}
                    size="body-sm"
                    font="grotesk"
                    weight="book"
                    color={accentColor}
                    className="opacity-60"
                  />
                )}
                <div className="flex items-end gap-[2px] pt-8">
                  <Typography
                    text={plan.monthlyCost}
                    size="h3"
                    font="grotesk"
                    weight="medium"
                    color={accentColor}
                  />
                  {plan.priceSubscript && (
                    <Typography
                      text={plan.priceSubscript}
                      size="body-md"
                      font="grotesk"
                      weight="medium"
                      color={accentColor}
                      className="pb-10"
                    />
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-16">
              {priceSubscript && (
                <Typography
                  text={priceSubscript}
                  size="body-md"
                  font="grotesk"
                  weight="book"
                  color={accentColor}
                  highlightedText={priceSubscriptHighlight}
                  highlightColor={priceSubscriptHighlightColor}
                  highlightWeight="medium"
                />
              )}
              <div className="flex items-center gap-12">
                <PillButton
                  style="solid"
                  size="medium"
                  color={accentColor}
                  text={primaryCtaText}
                  linkUrl={primaryCtaLink}
                />
                {secondaryCtaText && secondaryCtaLink && (
                  <TextButton
                    style="text"
                    text={secondaryCtaText}
                    textSize="body-md"
                    color={Color.Blue}
                    linkUrl={secondaryCtaLink}
                    uppercase={false}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

export default PlansBanner
